import { LoadingButton } from "@mui/lab";
import { Box, Stack, Typography } from "@mui/material";
import { ArrowForwardRounded } from "@mui/icons-material";

import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";

import { usePacks } from "../Packs/usePacks";
import { createState } from "@/global/store/zustand_store";
import { useFilterApis } from "../../GameFilters/useFilterApis";
import { useTicketmaster } from "@/global/hooks/useTicketmaster";
import { useInternalAuth } from "@/global/hooks/useInternalAuth";
import { useTicketsdotcom } from "@/global/hooks/useTicketsdotcom";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { useFraudPrevention } from "@/global/context/FraudPreventionProvider";

import type { PacksType } from "@/types";

export const usePacksModal = createState({
  isOpen: false,
  hasModalOpened: false,
});

export const PacksModal = () => {
  /** states */
  const modalState = usePacksModal();
  const { sm } = useCssMediaQuery("down");
  const { redirectLoading } = useInternalAuth();
  const { oktaAuthLoading } = useTicketsdotcom();
  const { getInventoryFilters } = useFilterApis();
  const { checkBrokerEmail } = useFraudPrevention();
  const { isLoading: isAuthLoading } = useTicketmaster();
  const {
    packs,
    selectedPack,
    isPacksEnabled,
    handleSelectPack,
    confirmEventSelectionLossTitle,
  } = usePacks();

  /** functions */
  function handleGetPackMessage(): string {
    if (!selectedPack || !selectedPack.selected) return "";
    return selectedPack.offerMoreMessage as string;
  }

  function onClickYes() {
    if (!isPacksEnabled || !selectedPack?.selected) return;
    const packId = selectedPack.id + 1;
    handleSelectPack(packId);
    modalState.set({ isOpen: false, hasModalOpened: true });
    const scrollToTopWhenSelecting = (packs as PacksType).list.find(
      (itm) => itm.id === packId
    )?.scrollToTopWhenSelecting;
    if (scrollToTopWhenSelecting)
      window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const handleConfirm = async () => {
    modalState.set({ isOpen: false });
    const cartConfirmBtn = document.getElementById(
      "confirm-game-selection-btn"
    );
    cartConfirmBtn?.click();
  };

  if (!isPacksEnabled) return null;

  return (
    <NotificationModal
      hideTitleIcon
      hidePrimaryButton
      hideSecondaryButton
      title={confirmEventSelectionLossTitle}
      isOpen={modalState.isOpen}
      onClose={() => modalState.set({ isOpen: false, hasModalOpened: true })}
      paperProps={{ style: { marginLeft: 2, marginRight: 2 } }}
      renderBody={() => (
        <Box>
          <Box p={sm ? 2 : 3}>
            <Box>
              <Typography variant="body1">{handleGetPackMessage()}</Typography>
            </Box>
          </Box>
        </Box>
      )}
      renderFooter={() => {
        return (
          <Stack
            py={2}
            px={2}
            gap={2}
            width={"100%"}
            justifyContent={"flex-end"}
            direction={"row"}
          >
            <LoadingButton
              loading={
                isAuthLoading ||
                getInventoryFilters.isLoading ||
                checkBrokerEmail.isLoading
              }
              size={"large"}
              disabled={
                isAuthLoading ||
                getInventoryFilters.isLoading ||
                checkBrokerEmail.isLoading ||
                oktaAuthLoading ||
                redirectLoading
              }
              variant={"outlined"}
              endIcon={<ArrowForwardRounded />}
              onClick={handleConfirm}
              sx={{
                width: sm ? "fit-content" : "180px",
                fontWeight: 600,
              }}
            >
              Continue
            </LoadingButton>

            <ModalActionButton
              size="small"
              variant="primary"
              onClick={onClickYes}
              style={{
                width: sm ? "fit-content" : "180px",
                fontWeight: 600,
                borderRadius: 99,
              }}
            >
              YES!
            </ModalActionButton>
          </Stack>
        );
      }}
    />
  );
};
